'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import useGetRepProspectLists from 'src/hooks/manager/useGetRepProspectLists';
import {
  useGetAllTerritoriesQuery,
  useLazyGetTerritoryQuery,
} from 'src/api/redux/territories/territoryApi';
import { useGetListsQuery } from 'src/api/redux/lists/listsApi';
import { CircularProgress, EmptyState, Checkbox } from 'src/components';
import { useLogging } from 'src/context/logging';
import { useAuth } from 'src/context/auth';
import { useDiscoveryMap } from 'src/context/map';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faMap, faList, faUser } from '@fortawesome/pro-regular-svg-icons';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from 'tailwind.config.js';

interface RepsTerritoriesAndListsProps {
  viewSource?: string;
  setFormattedProspectLists?: (prospectLists: any) => void;
  isActive?: boolean;
}

interface TerritoryGroup {
  ownerId: string;
  ownerName: string;
  territories: MedScout.Territory[];
}


interface List {
  id: number;
  name: string;
  owner: {
    id: number;
  };
}

const RepsTerritoriesAndLists = ({
  viewSource = 'managerOverview',
  setFormattedProspectLists,
  isActive = true,
}: RepsTerritoriesAndListsProps) => {
  const { user } = useAuth();
  const log = useLogging();

  const [checkedTerritories, setCheckedTerritories] = useState<string[]>([]);
  const [checkedLists, setCheckedLists] = useState<string[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [isAssignedSelected, setIsAssignedSelected] = useState(false);
  const [isFetchingAssigned, setIsFetchingAssigned] = useState(false);

  const { existingTerritories, setExistingTerritories, editingTerritory } =
    useDiscoveryMap();

  // API calls
  const { data: allTerritoriesData, isLoading: isTerritoryListLoading } =
    useGetAllTerritoriesQuery();
  const [fetchTerritoryDetails] = useLazyGetTerritoryQuery();
  const { data: providerLists, isLoading: isProviderListsLoading } =
    useGetListsQuery({});
  const { data: prospectLists, isLoading: isProspectListsLoading } =
    useGetRepProspectLists(checkedLists);

  const isLoading =
    isTerritoryListLoading || isProviderListsLoading || isProspectListsLoading;

  // Get primary color palettes (excluding neutrals/greys)
  const tailwindColors = useMemo(() => {
    const fullConfig = resolveConfig(tailwindConfig);
    const colors = fullConfig.theme?.colors || {};

    const primaryPalettes = [
      'red',
      'orange',
      'amber',
      'yellow',
      'lime',
      'green',
      'emerald',
      'teal',
      'cyan',
      'sky',
      'blue',
      'indigo',
      'violet',
      'purple',
      'fuchsia',
      'pink',
      'rose',
    ];

    const colorSet = primaryPalettes.flatMap((paletteName) => {
      const palette = colors[paletteName];
      if (typeof palette === 'object') {
        const shades = ['500', '600', '700', '800'];
        return shades
          .map((shade) => palette[shade])
          .filter(
            (color) => typeof color === 'string' && color.startsWith('#')
          );
      }
      return [];
    });

    return colorSet.sort(() => Math.random() - 0.5);
  }, []);

  // Handle tab activation
  useEffect(() => {
    if (isActive) {
      setIsTabLoading(true);
      const timer = setTimeout(() => {
        setIsTabLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isActive]);

  // Handle initial load
  useEffect(() => {
    if (!isLoading && !isInitialLoad) return;

    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [isLoading, isInitialLoad]);

  // Initialize checked territories from existing territories in map context on mount
  useEffect(() => {
    if (existingTerritories?.length > 0) {
      const territoryIds = existingTerritories.map((t) => t.id.toString());
      setCheckedTerritories(territoryIds);
    }
  }, [existingTerritories?.length]); // Only when existingTerritories length changes

  // Update formatted prospect lists
  useEffect(() => {
    if ((!prospectLists || isLoading) && checkedLists.length === 0) {
      setFormattedProspectLists?.([]);
      return;
    }
    setFormattedProspectLists?.(prospectLists);
    return () => setFormattedProspectLists?.([]);
  }, [prospectLists, isLoading, checkedLists.length]);

  const groupedTerritories = useMemo(() => {
    if (!allTerritoriesData) return [];

    const grouped = allTerritoriesData.reduce(
      (acc: Record<string, TerritoryGroup>, territory) => {
        if (!acc[territory.owner.id.toString()]) {
          acc[territory.owner.id.toString()] = {
            ownerId: territory.owner.id.toString(),
            ownerName: `${territory.owner.first_name} ${territory.owner.last_name}`,
            territories: [],
          };
        }
        acc[territory.owner.id.toString()].territories.push(territory);
        return acc;
      },
      {}
    );

    const groupedArray = Object.values(grouped) as TerritoryGroup[];
    const userTerritoriesIndex = groupedArray.findIndex(
      (group) => group.ownerId === user.id.toString()
    );

    if (userTerritoriesIndex !== -1) {
      const userTerritories = groupedArray.splice(userTerritoriesIndex, 1);
      groupedArray.unshift(...userTerritories);
    }

    return groupedArray;
  }, [allTerritoriesData, user.id]);

  const lists = providerLists?.results as List[];

  const toggleTerritory = useCallback(
    async (territory: MedScout.Territory) => {
      const territoryId = territory.id.toString();
      if (checkedTerritories.includes(territoryId)) {
        // Unchecking - remove from map
        setCheckedTerritories(
          checkedTerritories.filter((t) => t !== territoryId)
        );
        setExistingTerritories(
          existingTerritories.filter((t) => t.id !== territory.id)
        );
      } else {
        // Checking - fetch full territory details with polygons
        try {
          const result = await fetchTerritoryDetails({ territoryId });
          if (result.data) {
            const randomIndex = Math.floor(
              Math.random() * tailwindColors.length
            );
            const newColor = tailwindColors[randomIndex] || '#0ea5e9';
            const newTerritory: MedScout.Territory = {
              ...result.data,
              color: newColor as string,
            };

            setCheckedTerritories([...checkedTerritories, territoryId]);
            setExistingTerritories([...existingTerritories, newTerritory]);
          }
        } catch (error) {
          console.error('Failed to fetch territory details:', error);
        }
      }
    },
    [
      checkedTerritories,
      existingTerritories,
      tailwindColors,
      fetchTerritoryDetails,
      setExistingTerritories,
    ]
  );

  const toggleList = useCallback(
    (selectedList: List) => {
      const listId = selectedList.id.toString();
      const newCheckedValue = !checkedLists.includes(listId);
      log.event(`${viewSource}ListToggled`, {
        id: listId,
        value: newCheckedValue,
      });

      if (checkedLists.includes(listId)) {
        setCheckedLists(checkedLists.filter((t) => t !== listId));
      } else {
        setCheckedLists([...checkedLists, listId]);
      }
    },
    [checkedLists, viewSource]
  );

  const getAssignedTerritories = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      setIsAssignedSelected(isChecked);
      const assigned = allTerritoriesData?.filter((t) => t.is_canonical) || [];
      const assignedIds = assigned.map((t) => t.id.toString());

      if (isChecked) {
        setIsFetchingAssigned(true);

        // Add assigned territories to checked list
        const newCheckedIds = new Set([...checkedTerritories, ...assignedIds]);
        setCheckedTerritories(Array.from(newCheckedIds));

        // Fetch full territory details for each assigned territory
        const newMapTerritories = [...existingTerritories];
        for (const territory of assigned) {
          if (!existingTerritories.find((et) => et.id === territory.id)) {
            try {
              const result = await fetchTerritoryDetails({
                territoryId: territory.id.toString(),
              });
              if (result.data) {
                const randomIndex = Math.floor(
                  Math.random() * tailwindColors.length
                );
                const newColor = tailwindColors[randomIndex] || '#0ea5e9';
                newMapTerritories.push({
                  ...result.data,
                  color: newColor as string,
                });
              }
            } catch (error) {
              console.error(
                `Failed to fetch territory ${territory.id}:`,
                error
              );
            }
          }
        }
        setExistingTerritories(newMapTerritories);
        setIsFetchingAssigned(false);
      } else {
        // Remove assigned territories from checked list
        const newCheckedIds = checkedTerritories.filter(
          (t) => !assignedIds.includes(t)
        );
        setCheckedTerritories(newCheckedIds);

        // Remove assigned territories from map
        const newMapTerritories = existingTerritories.filter(
          (t) => !assignedIds.includes(t.id.toString())
        );
        setExistingTerritories(newMapTerritories);
      }
    },
    [
      allTerritoriesData,
      checkedTerritories,
      existingTerritories,
      tailwindColors,
      fetchTerritoryDetails,
      setExistingTerritories,
    ]
  );

  const showLoading = isInitialLoad || isTabLoading;

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="sticky top-0 bg-white z-10 border-b border-neutral-100 shadow-sm">
        <div className="flex items-center justify-between w-full py-4 px-6 bg-gradient-to-r from-sky-50 to-white">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm">
                <Icon icon={faUser} className="text-sky-600" />
              </div>
              <div className="flex flex-col">
                <h2 className="text-base font-semibold text-neutral-900">
                  Your Team
                </h2>
                <p className="text-xs text-neutral-500">
                  Manage territories and prospect lists
                </p>
              </div>
            </div>
            {isLoading && (
              <div className="flex items-center justify-center ">
                <CircularProgress size="sm" color="neutral" />
              </div>
            )}
          </div>
        </div>
      </div>

      {showLoading ? (
        <div className="flex items-center justify-center p-6 min-h-96">
          <CircularProgress size="sm" color="neutral" />
        </div>
      ) : !groupedTerritories?.length ? (
        <EmptyState
          title="No Territories Available"
          description="There are no territories to display."
          icon={<Icon icon={faMap} className="text-6xl text-neutral-400" />}
        />
      ) : (
        <div className="flex-1 overflow-y-auto px-4 space-y-3">
          <div className="flex items-center gap-2 p-2 bg-neutral-50 rounded">
            <Checkbox
              checked={isAssignedSelected}
              onChange={getAssignedTerritories}
              size="sm"
              disabled={isFetchingAssigned}
            />
            <span className="text-sm font-medium text-neutral-700">
              Select primary territories
            </span>
            {isFetchingAssigned && (
              <CircularProgress size="sm" color="neutral" />
            )}
          </div>

          <div className="space-y-3">
            {groupedTerritories.map((territoryGroup) => {
              const filteredLists = lists?.filter(
                (list) => list.owner.id.toString() === territoryGroup.ownerId
              );

              return (
                <div
                  key={territoryGroup.ownerId}
                  className="space-y-2 p-3 bg-white rounded border border-neutral-100"
                >
                  <div className="flex items-center gap-2">
                    <Icon icon={faUser} className="text-neutral-500" />
                    <h3 className="text-sm font-semibold text-neutral-800">
                      {territoryGroup.ownerName}
                    </h3>
                  </div>

                  <div className="space-y-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Icon icon={faList} className="text-neutral-400" />
                        <h4 className="text-sm font-medium text-neutral-600">
                          Lists
                        </h4>
                      </div>
                      {filteredLists?.length > 0 ? (
                        <div className="space-y-1 pl-4">
                          {filteredLists.map((list) => (
                            <div
                              key={list.id}
                              className="flex items-center gap-2 p-1 hover:bg-neutral-50 rounded transition-colors"
                            >
                              <Checkbox
                                checked={checkedLists.includes(
                                  list.id.toString()
                                )}
                                onChange={() => toggleList(list)}
                                size="sm"
                              />
                              <span className="text-sm text-neutral-700">
                                {list.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState
                          title="No Lists Available"
                          description="This user doesn't have any lists to display."
                          icon={
                            <Icon
                              icon={faList}
                              className="text-4xl text-neutral-400"
                            />
                          }
                          className="p-2"
                        />
                      )}
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Icon icon={faMap} className="text-neutral-400" />
                        <h4 className="text-sm font-medium text-neutral-600">
                          Territories
                        </h4>
                      </div>
                      {territoryGroup.territories?.length > 0 ? (
                        <div className="space-y-1 pl-4">
                          {territoryGroup.territories
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .sort(
                              (a, b) =>
                                Number(b.is_canonical) - Number(a.is_canonical)
                            )
                            .map((t) => {
                              const isEditing = editingTerritory?.id === t.id;
                              const label = isEditing
                                ? `${t.name} (editing)`
                                : t.name;
                              return (
                                <div
                                  key={t.id}
                                  className="flex items-center gap-2 p-1 hover:bg-neutral-50 rounded transition-colors"
                                >
                                  <Checkbox
                                    checked={
                                      checkedTerritories.includes(
                                        t.id.toString()
                                      ) || isEditing
                                    }
                                    onChange={
                                      isEditing
                                        ? undefined
                                        : () => toggleTerritory(t)
                                    }
                                    size="sm"
                                    className={
                                      isEditing
                                        ? 'opacity-50 cursor-not-allowed'
                                        : ''
                                    }
                                  />
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-neutral-700">
                                      {label}
                                    </span>
                                    {t.is_canonical && (
                                      <span className="text-xs font-medium text-neutral-500">
                                        (primary territory)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <EmptyState
                          title="No Territories Available"
                          description="This user doesn't have any territories to display."
                          icon={
                            <Icon
                              icon={faMap}
                              className="text-4xl text-neutral-400"
                            />
                          }
                          className="p-2"
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="h-48" />
        </div>
      )}
    </div>
  );
};

export default RepsTerritoriesAndLists;
